import React, {useCallback, useContext, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {HighlightedTitle} from 'components/Home/HighlightedTitle'
import {
  getIsRtlLanguage,
  getLanguageCode,
  getUserCountryCode
} from 'modules/meta/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {SmoothScrollNavigation} from '@vio/atlas/helpers/SmoothScrollNavigation'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Team
} from '@vio/core/src/analytics/types/Events'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@vio/core/src/auth/modules/actions'
import {getBookingManagementUrl} from '@vio/core/src/brand/utils'
import {roomsToString} from '@vio/core/src/room/business/roomConfiguration'

import {Title} from '../components/Title'
import {CarouselWidgetsContext} from '../context/CarouselWidgetsContext'
import {Booking} from './components/Booking'

const Wrapper = styled.div<{fullWidth: boolean}>`
  width: ${({fullWidth}) => (fullWidth ? '100%' : '228px')};
  min-width: ${({fullWidth}) => (fullWidth ? '100%' : '')};
  min-height: 90px;
`

export const YourStays = () => {
  const {brand} = useBrand()
  const appEnv = process.env.NEXT_PUBLIC_ENV || ''
  const {isMobile} = useDeviceLayout()
  const isRtlLanguage = useSelector(getIsRtlLanguage)
  const languageCode = useSelector(getLanguageCode)
  const userCountry = useSelector(getUserCountryCode)
  const dispatch = useDispatch()

  const {bookings} = useContext(CarouselWidgetsContext)

  const trackingPayload = {
    component: 'YourStays',
    team: Team.Retention
  }

  useEffect(() => {
    if (bookings?.length) {
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Carousel,
          action: Action.Displayed,
          payload: {
            totalNumber: bookings?.length
          },
          ...trackingPayload
        })
      )
    }
  }, [bookings, dispatch])

  const handleBookingClick = useCallback(
    (booking, index) => {
      setTimeout(() => {
        dispatch(
          trackEvent({
            category: Category.User,
            entity: Entity.CarouselCard,
            action: Action.Clicked,
            ...trackingPayload,
            payload: {
              totalNumber: bookings.length,
              index,
              searchItem: {
                hotelId: booking.hotelId,
                checkIn: booking.checkIn,
                checkOut: booking.checkOut,
                rooms: roomsToString(booking.rooms)
              }
            }
          })
        )
        const bookingManagementUrl = getBookingManagementUrl({
          brand,
          appEnv,
          bookingId: booking.id,
          locale: languageCode,
          userCountry,
          extraParams: {showBackButton: 'true'}
        })
        window.location.href = bookingManagementUrl
      }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
    },
    [dispatch, bookings, brand, appEnv, languageCode, userCountry]
  )

  if (!bookings?.length) return null

  return (
    <div>
      <Title variant="titleXL">
        <HighlightedTitle source="YourStays" />
      </Title>
      <SmoothScrollNavigation
        isRtlLanguage={isRtlLanguage}
        hasSidePaddings={isMobile}
      >
        {bookings.map((booking, index) => (
          <Wrapper key={`bookings-${booking.id}`} fullWidth={isMobile}>
            <Booking
              fullWidth={isMobile}
              hotelId={booking.hotelId}
              checkIn={booking.checkIn}
              checkOut={booking.checkOut}
              rooms={booking.rooms}
              onClick={() => handleBookingClick(booking, index)}
            />
          </Wrapper>
        ))}
      </SmoothScrollNavigation>
    </div>
  )
}
